<template>

  <div class="wizard-step-compact">

    <!-- Q1: Location — no step-title / SATTVA LOCATION strip; the stepper

         already names this step. -->

    <div class="wizard-question wizard-accordion-section">

      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'location' ? 'true' : 'false'"
        @click="toggleSection('location')"
      >
        <h5 class="wizard-question-heading">Which Sattva GST / issuing location do you want to bill from?</h5>
        <feather-icon :icon="openSection === 'location' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <b-collapse :visible="openSection === 'location'">

      <v-select

        id="location-select"

        v-model="formData.location"

        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"

        label="title"

        :options="locations"

        placeholder="Select issuing office"

        :reduce="o => o.title"

        @input="onLocationSelected"

      />

      <small v-if="errors.location" class="text-danger d-block mt-25">{{ errors.location }}</small>
      </b-collapse>
    </div>



    <!-- Q2: Confirm Bill From — only after a location is chosen.

         Single heading + summary card (no repeated BILL FROM section header). -->

    <div class="wizard-question wizard-accordion-section" v-if="formData.location">

      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'confirm' ? 'true' : 'false'"
        @click="toggleSection('confirm')"
      >
        <h5 class="wizard-question-heading">Confirm Bill From details?</h5>
        <feather-icon :icon="openSection === 'confirm' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <b-collapse :visible="openSection === 'confirm'">

      <b-card class="wizard-summary-card mb-0" body-class="py-2 px-2">

        <div class="d-flex justify-content-between align-items-start">

          <div class="flex-grow-1 min-w-0">

            <div class="d-flex align-items-center flex-wrap mb-1">

              <b-badge v-if="sattvaInfoReady && !sattvaDetailsUnavailable" variant="light-success" class="mr-1">Auto-filled</b-badge>

              <b-badge v-else-if="sattvaDetailsUnavailable" variant="light-warning" class="mr-1">Not available</b-badge>

              <span class="small text-muted">{{ formData.location }}</span>

            </div>

            <div v-if="sattvaInfoReady" class="wizard-summary-lines">

              <div><strong>{{ formData.invoiceSattvaInfo.companyName }}</strong></div>

              <div v-if="formData.invoiceSattvaInfo.gst" class="text-muted">GST: {{ formData.invoiceSattvaInfo.gst }} · PAN: {{ formData.invoiceSattvaInfo.pan || '—' }}</div>

              <div v-if="formData.invoiceSattvaInfo.bankName" class="text-muted">{{ formData.invoiceSattvaInfo.bankName }} · IFSC {{ formData.invoiceSattvaInfo.ifscCode || '—' }}</div>

              <div v-if="formData.invoiceSattvaInfo.address" class="text-muted text-truncate">{{ truncate(formData.invoiceSattvaInfo.address, 80) }}</div>

            </div>

            <div v-else-if="sattvaDetailsLoading" class="small text-muted">

              <b-spinner small class="mr-1" />Loading company details for {{ formData.location }}…

            </div>

            <div v-else-if="sattvaDetailsUnavailable" class="small text-muted">

              Company/bank details for "{{ formData.location }}" aren't set up yet —

              <b-button variant="link" size="sm" class="p-0 align-baseline" @click="editSattvaDetails = true">enter them manually</b-button>.

              Use <strong>Save company details</strong> below to persist them for future invoices.

            </div>

          </div>

          <b-button

            v-if="sattvaInfoReady || sattvaDetailsUnavailable"

            variant="link"

            size="sm"

            class="p-0 ml-2 flex-shrink-0"

            @click="editSattvaDetails = !editSattvaDetails"

          >

            {{ editSattvaDetails ? 'Hide' : 'Edit' }}

          </b-button>

        </div>

        <b-collapse v-model="editSattvaDetails" class="mt-2">

          <b-row class="pt-2 border-top">

            <b-col cols="12">

              <b-form-group label="Company Name" label-class="small mb-0"><b-form-input v-model="formData.invoiceSattvaInfo.companyName" size="sm" @input="markSattvaEdited" /></b-form-group>

              <b-form-group label="GST" label-class="small mb-0"><b-form-input v-model="formData.invoiceSattvaInfo.gst" size="sm" @input="markSattvaEdited" /></b-form-group>

              <b-form-group label="PAN" label-class="small mb-0"><b-form-input v-model="formData.invoiceSattvaInfo.pan" size="sm" @input="markSattvaEdited" /></b-form-group>

              <b-form-group label="Address" label-class="small mb-0"><b-form-textarea v-model="formData.invoiceSattvaInfo.address" rows="2" size="sm" @input="markSattvaEdited" /></b-form-group>

              <b-form-group label="Bank Name" label-class="small mb-0"><b-form-input v-model="formData.invoiceSattvaInfo.bankName" size="sm" @input="markSattvaEdited" /></b-form-group>

              <b-form-group label="Account Name" label-class="small mb-0"><b-form-input v-model="formData.invoiceSattvaInfo.accountName" size="sm" @input="markSattvaEdited" /></b-form-group>

              <b-form-group label="Account Number" label-class="small mb-0"><b-form-input v-model="formData.invoiceSattvaInfo.accountNumber" size="sm" @input="markSattvaEdited" /></b-form-group>

              <b-form-group label="Account Type" label-class="small mb-0"><b-form-input v-model="formData.invoiceSattvaInfo.accountType" size="sm" placeholder="e.g. Current Account" @input="markSattvaEdited" /></b-form-group>

              <b-form-group label="IFSC Code" label-class="small mb-0"><b-form-input v-model="formData.invoiceSattvaInfo.ifscCode" size="sm" @input="markSattvaEdited" /></b-form-group>

              <!-- SAC / HSN Code and LUT No. are separate fields on More Details
                   — see WizardMoreDetailsStep.vue. -->

            </b-col>

          </b-row>

        </b-collapse>



        <!-- Persist to invoice_sattva_company_details (master), not just this invoice -->

        <div

          v-if="showSaveCompanyDetails"

          class="d-flex flex-wrap align-items-center mt-2 pt-2 border-top"

        >

          <b-button

            variant="outline-primary"

            size="sm"

            class="mr-2 mb-1"

            :disabled="!canSaveCompanyDetails || sattvaSaveLoading"

            @click="$emit('save-company-details', { proceedNext: false })"

          >

            <b-spinner v-if="sattvaSaveLoading" small class="mr-1" />

            Save company details

          </b-button>

          <b-form-checkbox

            v-model="autoSaveOnNext"

            class="small mb-1"

            size="sm"

          >

            Auto-save on Next when filled

          </b-form-checkbox>

          <small class="text-muted d-block w-100 mt-25">

            Saves to the Sattva company master for this location (future invoices). Invoice create still works if save fails.

          </small>

        </div>

      </b-card>
      </b-collapse>
    </div>



    <div class="wizard-nav-actions d-flex justify-content-between align-items-center w-100 flex-wrap">

      <b-button

        variant="outline-secondary"

        size="sm"

        class="wizard-icon-btn"

        aria-label="Back"

        @click="$emit('back')"

      >

        <feather-icon icon="ArrowLeftIcon" size="16" />

      </b-button>

      <b-button

        variant="primary"

        size="sm"

        class="wizard-icon-btn"

        aria-label="Next: Add Line Items"

        :disabled="sattvaSaveLoading"

        @click="onNext"

      >

        <feather-icon icon="ArrowRightIcon" size="16" />

      </b-button>

      <small v-if="!isValid" class="text-muted d-block mt-1 w-100">Select a Sattva Location to continue.</small>

    </div>

  </div>

</template>



<script>

import {

  BCard, BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BButton,

  BSpinner, BCollapse, BBadge,

} from 'bootstrap-vue'

import vSelect from 'vue-select'



export default {

  name: 'WizardSattvaLocationStep',

  components: {

    BCard, BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BButton,

    BSpinner, BCollapse, BBadge, vSelect,

  },

  props: {

    formData: { type: Object, required: true },

    errors: { type: Object, default: () => ({}) },

    locations: { type: Array, default: () => [] },

    sattvaDetailsLoading: { type: Boolean, default: false },

    sattvaDetailsUnavailable: { type: Boolean, default: false },

    sattvaSaveLoading: { type: Boolean, default: false },

  },

  data() {

    return {

      editSattvaDetails: false,

      sattvaManuallyEdited: false,

      autoSaveOnNext: true,
      openSection: 'location',

    }

  },

  computed: {

    isValid() {

      return !!this.formData.location

    },

    sattvaInfoReady() {

      const s = this.formData.invoiceSattvaInfo

      return !!(s && (s.companyName || s.gst))

    },

    showSaveCompanyDetails() {

      // Explicit DB save when master was missing or user edited Bill From.

      return !!(

        this.formData.location

        && (this.sattvaDetailsUnavailable || this.sattvaManuallyEdited || this.editSattvaDetails)

        && (this.sattvaInfoReady || this.editSattvaDetails)

      )

    },

    canSaveCompanyDetails() {

      const s = this.formData.invoiceSattvaInfo || {}

      return !!(this.formData.location && (s.companyName || s.gst))

    },

  },

  watch: {

    'formData.location'() {

      this.sattvaManuallyEdited = false

      this.editSattvaDetails = false

    },

    sattvaDetailsUnavailable(val) {

      if (val) this.editSattvaDetails = true

    },

  },

  methods: {

    toggleSection(key) {
      this.openSection = this.openSection === key ? '' : key
    },

    onLocationSelected() {
      this.openSection = 'confirm'
      this.$emit('location-selected', this.formData.location)
    },

    markSattvaEdited() {

      this.sattvaManuallyEdited = true

    },

    truncate(str, max) {

      if (!str) return ''

      const s = String(str).replace(/\s+/g, ' ').trim()

      return s.length <= max ? s : `${s.slice(0, max)}…`

    },

    onNext() {

      if (

        this.autoSaveOnNext

        && this.canSaveCompanyDetails

        && (this.sattvaDetailsUnavailable || this.sattvaManuallyEdited)

      ) {

        this.$emit('save-company-details', { proceedNext: true })

        return

      }

      this.$emit('next')

    },

  },

}

</script>



<style src="./wizard-step-shared.css"></style>

<style scoped>

.min-w-0 {

  min-width: 0;

}

</style>


